import { type FC, useState, useRef, useMemo, useEffect } from 'react';

import { _notNil, _isEmptyString, _isNil } from '@/littledash';

import type { SampleType, SampleTypeRow } from '@/model/SampleType.model';
import type { components } from '@/generated/internal-api/openapi-types';
import type { ValidationData } from '@/utils/sampleTypeValidation';

import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { successToast, errorToast } from '@/helpers';
import { ApiService } from '@/support/ApiService.ts';
import InVivoError from '@/model/InVivoError.ts';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';

import BulkEditSampleTypesTable from './BulkEditSampleTypesTable';

type SampleUpdatePayloadItem = components['schemas']['TeamGlossarySamplesUpdateItemV1.schema'];

interface HotTableRefLike {
  hotInstance?: {
    getData?: () => Array<SampleTypeRow>;
  } | null;
}

interface BulkEditSampleTypesProps {
  sampleTypes: Array<SampleType>;
  onSave: () => void;
  onCancel: () => void;
}

const BulkEditSampleTypes: FC<BulkEditSampleTypesProps> = ({ sampleTypes, onSave, onCancel }) => {
  const [saving, setSaving] = useState(false);
  const [validationData, setValidationData] = useState<ValidationData | null>(null);
  const [loadingValidation, setLoadingValidation] = useState(true);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const hotTableRef = useRef<HotTableRefLike | null>(null);

  const availableDetailOptions = useMemo(() => {
    return ['Weight (mg g µg)', 'Volume (ml µl cm³ mm³)', 'Width (mm)', 'Length (mm)', 'Depth (mm)'];
  }, []);

  useEffect(() => {
    const fetchValidationData = async (): Promise<void> => {
      try {
        const response = await ApiService.call({
          endpoint: 'GET /api/v1/team-glossary/sample-validation-data',
          path: undefined,
          options: {
            onError: { toast: false, throw: true, capture: false, slug: 'bulk-edit-sample-types-validation-load' },
          },
        });
        setValidationData(response.body as ValidationData);
      } catch (error) {
        errorToast('Failed to load validation data');
        ExceptionHandler.captureException(
          new InVivoError('Failed to load validation data', {
            cause: error,
            slug: 'bulk-edit-sample-types-validation-load',
          })
        );
        setValidationData(null);
      } finally {
        setLoadingValidation(false);
      }
    };

    void fetchValidationData();
  }, []);

  const displayToApiMap: Record<string, string> = useMemo(
    () => ({
      'Weight (mg g µg)': 'weight',
      'Volume (ml µl cm³ mm³)': 'volume',
      'Width (mm)': 'width',
      'Length (mm)': 'length',
      'Depth (mm)': 'depth',
    }),
    []
  );

  const apiToDisplayMap: Record<string, string> = useMemo(
    () => ({
      weight: 'Weight (mg g µg)',
      volume: 'Volume (ml µl cm³ mm³)',
      width: 'Width (mm)',
      length: 'Length (mm)',
      depth: 'Depth (mm)',
    }),
    []
  );

  const initialData: Array<SampleTypeRow> = useMemo(() => {
    return sampleTypes.map((sampleType) => {
      const detailsApiValues = sampleType.options?.details ?? [];
      const detailsDisplayLabels = detailsApiValues.map((apiValue) => apiToDisplayMap[apiValue] ?? apiValue);
      const joinedDetails = detailsDisplayLabels.join(', ');
      return [sampleType.title, sampleType.label ?? '', joinedDetails];
    });
  }, [sampleTypes, apiToDisplayMap]);

  const handleSave = async () => {
    const hotInstance = hotTableRef.current?.hotInstance;
    if (_isNil(hotInstance)) {
      errorToast('Unable to access table data');
      return;
    }

    setSaving(true);

    try {
      const currentData = (hotInstance.getData?.() ?? []) as Array<SampleTypeRow>;

      const teamGlossariesPayload = currentData.reduce<Array<SampleUpdatePayloadItem>>((acc, row, index) => {
        const [title, label, detailsRaw] = row;
        const sampleType = sampleTypes[index];

        const isSystem = sampleType.system === true;
        const isArchived = _notNil(sampleType.archived_at);
        if (isSystem || isArchived) {
          return acc;
        }

        const detailsDisplay = Array.isArray(detailsRaw)
          ? detailsRaw.map((d) => String(d).trim()).filter(Boolean)
          : _notNil(detailsRaw)
            ? String(detailsRaw)
                .split(',')
                .map((d) => d.trim())
                .filter(Boolean)
            : [];
        const detailsApi = detailsDisplay
          .map((d) => displayToApiMap[d])
          .filter(
            (d): d is 'volume' | 'weight' | 'width' | 'length' | 'depth' =>
              d !== undefined && ['volume', 'weight', 'width', 'length', 'depth'].includes(d)
          );
        const maybeTitle = (title ?? '').toString().trim();
        const maybeLabel = (label ?? '').toString().trim();

        acc.push({
          team_glossary_api_id: sampleType.api_id,
          title: maybeTitle,
          options: { details: detailsApi },
          ...(_notNil(maybeLabel) && !_isEmptyString(maybeLabel) && { label: maybeLabel }),
        });
        return acc;
      }, []);

      await ApiService.call({
        endpoint: 'PATCH /api/v1/team-glossary',
        path: undefined,
        body: { team_glossaries: teamGlossariesPayload },
        options: { onError: { toast: false, throw: true, capture: false, slug: 'bulk-edit-sample-types-save' } },
      });

      successToast('Sample types updated successfully');

      onSave();
    } catch (error) {
      errorToast('Failed to update sample types');
      ExceptionHandler.captureException(
        new InVivoError('Failed to update sample types', {
          cause: error,
          slug: 'bulk-edit-sample-types-save',
        })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalContainer className="bg-white">
      <ModalHeader
        title="Edit sample types"
        closeModal={onCancel}
        closeDisabled={saving}
        className="pa3 bb b--moon-gray w-100"
      />

      <div className="pa3">
        {loadingValidation ? (
          <div className="tc pa4">
            <div className="mb2">Loading validation data...</div>
          </div>
        ) : (
          <BulkEditSampleTypesTable
            data={initialData}
            innerRef={hotTableRef}
            sampleTypes={sampleTypes}
            validationData={validationData}
            availableDetailOptions={availableDetailOptions}
            setInvalid={setHasValidationErrors}
          />
        )}
      </div>

      <ModalActions
        className="pa3 bt b--moon-gray w-100"
        onSubmit={handleSave}
        onCancel={onCancel}
        submitBtnText="Update"
        cancelBtnText="Close"
        submitButtonProps={{
          loading: saving,
          disabled: saving || loadingValidation || hasValidationErrors,
        }}
        cancelButtonProps={{
          disabled: saving,
        }}
      />
    </ModalContainer>
  );
};

export default BulkEditSampleTypes;
